import { useTranslation } from "@/lib/i18n";
import { useState } from "react";
import { Menu, X, Trophy, ChevronRight } from "lucide-react";

const LEAGUE_FLAGS = {
  POR: "https://flagcdn.com/w40/pt.png",
  ENG: "https://flagcdn.com/w40/gb-eng.png",
  SPA: "https://flagcdn.com/w40/es.png",
  GER: "https://flagcdn.com/w40/de.png",
  ITA: "https://flagcdn.com/w40/it.png",
  FRA: "https://flagcdn.com/w40/fr.png",
  NED: "https://flagcdn.com/w40/nl.png",
  BEL: "https://flagcdn.com/w40/be.png",
  TUR: "https://flagcdn.com/w40/tr.png",
  SCO: "https://flagcdn.com/w40/gb-sct.png",
  BRA: "https://flagcdn.com/w40/br.png",
  ARG: "https://flagcdn.com/w40/ar.png",
  UCL: "https://upload.wikimedia.org/wikipedia/en/b/bf/UEFA_Champions_League_logo_2.svg",
  UEL: "https://upload.wikimedia.org/wikipedia/en/b/b3/UEFA_Europa_League_logo_%282024%E2%80%93present%29.svg",
  UCO: "https://upload.wikimedia.org/wikipedia/en/5/58/UEFA_Europa_Conference_League_logo_%282021%29.svg",
  TCP: "https://flagcdn.com/w40/pt.png",
  FAC: "https://flagcdn.com/w40/gb-eng.png",
  CDR: "https://flagcdn.com/w40/es.png",
  CDF: "https://flagcdn.com/w40/fr.png",
  DFB: "https://flagcdn.com/w40/de.png",
  CPI: "https://flagcdn.com/w40/it.png",
  INT: "https://upload.wikimedia.org/wikipedia/en/a/a9/FIFA_logo.svg",
  WC26: "https://upload.wikimedia.org/wikipedia/en/thumb/8/8a/2026_FIFA_World_Cup_emblem.svg/800px-2026_FIFA_World_Cup_emblem.svg.png",
  NOR: "https://flagcdn.com/w40/no.png",
  SWE: "https://flagcdn.com/w40/se.png",
  MLS: "https://flagcdn.com/w40/us.png",
  KSA: "https://flagcdn.com/w40/sa.png",
  FIN: "https://flagcdn.com/w40/fi.png",
  LIB: "https://upload.wikimedia.org/wikipedia/en/thumb/5/5f/CONMEBOL_Libertadores_logo.svg/200px-CONMEBOL_Libertadores_logo.svg.png",
};

const CATEGORIES = {
  league: "🏆 Ligas Nacionais",
  europe: "⭐ Competições Europeias",
  cup: "🏴 Taças Nacionais",
  international: "🌍 Seleções",
  world_cup: "🌍 Seleções",
};

export default function LeagueMenu({ leagues, activeLeague, onSelect, loading }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const grouped = {};
  leagues.forEach((league) => {
    const cat = league.category || "league";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(league);
  });

  // Merge international and world_cup
  if (grouped.world_cup) {
    if (!grouped.international) grouped.international = [];
    grouped.international = [...grouped.international, ...grouped.world_cup];
    delete grouped.world_cup;
  }

  const activeLeagueObj = leagues.find((l) => l.code === activeLeague);

  const handleSelect = (code) => {
    onSelect(code);
    setOpen(false);
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 bg-surface border border-line rounded-sm hover:bg-surface-hover transition-colors"
        title={t('selectLeague')}
      >
        <Menu className="w-4 h-4 text-txt-secondary" />
        <span className="text-sm font-medium text-white">{t('competitions')}</span>
      </button>

      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-surface border-r border-line z-50 transform transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-line">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-volt" />
            <span className="font-heading text-sm font-bold uppercase tracking-wider text-white">
              {t('competitions')}
            </span>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-1.5 hover:bg-surface-hover rounded-sm transition-colors"
          >
            <X className="w-4 h-4 text-txt-secondary" />
          </button>
        </div>

        {/* League List */}
        <div className="overflow-y-auto h-[calc(100%-60px)] pb-6">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="mt-4">
              <p className="px-4 text-[10px] font-bold uppercase tracking-[0.2em] text-txt-secondary mb-1">
                {cat === 'league' ? `🏆 ${t('leaguesCat')}` : cat === 'europe' ? `⭐ ${t('europeCat')}` : cat === 'cup' ? `🏴 ${t('cupsCat')}` : `🌍 ${t('internationalCat')}`}
              </p>
              {items.map((league) => {
                const isActive = activeLeague === league.code;
                return (
                  <button
                    key={league.code}
                    onClick={() => handleSelect(league.code)}
                    disabled={loading}
                    className={`
                      w-full flex items-center justify-between px-4 py-2.5 transition-colors text-left
                      ${isActive
                        ? "bg-volt/10 text-volt"
                        : "text-txt-secondary hover:bg-surface-hover hover:text-white"
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={LEAGUE_FLAGS[league.code] || ""}
                        alt={league.code}
                        className="w-6 h-4 object-cover rounded-[2px] shrink-0"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                      <span className="text-sm font-medium">{league.name}</span>
                    </div>
                    {isActive && <ChevronRight className="w-4 h-4 text-volt" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
